import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ref, get, update } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaArrowLeft, FaPrint, FaList, FaBuilding, FaWrench, FaClipboardList } from 'react-icons/fa';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';

const sanitizeKey = (key) => {
  return key.replace(/[.#$/[\]]/g, '_');
};

const inputClass = "w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block p-3 transition-all hover:border-indigo-300 shadow-sm";
const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";
const thClass = "p-4 border-b border-gray-100 font-semibold text-gray-600 bg-gray-50/80";

const EntryForm = () => {
  const { division } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [company, setCompany] = useState('');
  const [numberOfJobs, setNumberOfJobs] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [commonData, setCommonData] = useState({
    challanNo: '',
    date: '',
    vehicleNo: '',
    totalOil: '',
    oilChallanNo: '',
  });

  const [formRows, setFormRows] = useState([]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const stateCompany = location.state?.company;
        const storedCompany = localStorage.getItem('selectedCompany') || localStorage.getItem('userCompany');

        if (stateCompany || storedCompany) {
          const normalizedCompany = normalizeCompanyKey(stateCompany || storedCompany);
          const sanitizedCompany = firebaseCompanyKey(normalizedCompany);
          
          const companyRef = ref(realtimeDb, `companies/${sanitizeKey(sanitizedCompany)}`);
          const companySnap = await get(companyRef);
          
          if (companySnap.exists()) {
            setCompany(sanitizedCompany);
            localStorage.setItem('selectedCompany', sanitizedCompany);
          } else {
            alert('Company not found. Please login again.');
            navigate('/login');
          }
        } else {
          alert('Company not found. Please login again.');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching company:', error);
        alert('Error loading company data. Please try again.');
        navigate('/login');
      }
    };

    fetchCompany();
  }, [location, navigate]);

  const handleCommonChange = (e) => {
    const { name, value } = e.target;
    setCommonData((prev) => ({
      ...prev,
      [name]: name === 'vehicleNo' ? value.toUpperCase() : value,
    }));
  };

  const handleRowChange = (index, field, value) => {
    const updated = [...formRows];
    updated[index][field] = value;
    setFormRows(updated);
  };

  const handleNumberOfJobsChange = (e) => {
    const value = e.target.value;
    setNumberOfJobs(value);
    const count = parseInt(value, 10) || 0;

    setFormRows(
      Array.from({ length: count }, (_, i) => ({
        ...(formRows[i] || {
          jobNo: '',
          kva: '',
          make: '',
          makeSrNo: '',
          type: 'CRGO',
          oilCap: '',
          kg: '',
        }),
      }))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const { challanNo, date, vehicleNo, totalOil, oilChallanNo } = commonData;

    const jobData = formRows.map((row, i) => ({
      ...row,
      challanNo,
      date,
      vehicleNo,
      totalOil,
      oilChallanNo,
      srNo: i + 1,
      completed: false,
      timestamp: Date.now(),
      entryDate: new Date().toISOString().split('T')[0],
      status: 'pending'
    }));

    const sanitizedCompany = sanitizeKey(company);
    const sanitizedDivision = sanitizeKey(division);
    const basePath = `companies/${sanitizedCompany}/${sanitizedDivision}`;
    const entryPath = `${basePath}/entryPass`;
    const registerPath = `${basePath}/register`;
    const counterPath = `${basePath}/metadata/entryCounter`;
    const ids = [];

    try {
      const counterRef = ref(realtimeDb, counterPath);
      const counterSnap = await get(counterRef);
      let counter = counterSnap.exists() ? counterSnap.val() : 0;

      const updates = {};
      
      for (const job of jobData) {
        counter += 1;
        const entryId = counter.toString();
        
        updates[`${entryPath}/${entryId}`] = job;
        updates[`${registerPath}/${entryId}`] = {
          ...job,
          isActive: true,
          lastUpdated: Date.now()
        };
        ids.push(entryId);
      }

      updates[counterPath] = counter;
      await update(ref(realtimeDb), updates);
      
      navigate(`/print/entrypass/${company}/${division}/${ids.join(',')}`);
    } catch (err) {
      console.error('Error saving entries:', err);
      alert(`Failed to save entry: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const { challanNo, date, vehicleNo, totalOil, oilChallanNo } = commonData;
    if (!challanNo || !date || !vehicleNo || !totalOil || !oilChallanNo) return false;

    return formRows.every(
      (row) =>
        row.jobNo && row.kva && row.make && row.makeSrNo && row.oilCap && row.kg
    );
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-all hover:shadow-md">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 rounded-full bg-gray-50 hover:bg-indigo-50 hover:text-indigo-600 text-gray-600 transition-colors"
            >
              <FaArrowLeft className="text-lg" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FaBuilding className="text-indigo-500" />
                {company}
              </h1>
              <h2 className="text-sm font-medium text-gray-500 flex items-center gap-2 mt-1">
                <FaWrench className="text-gray-400" />
                {division} <span className="mx-1">•</span> New Entry
              </h2>
            </div>
          </div>
          <button
            onClick={() => navigate(`/user/division/${division}/register`, { state: { company } })}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-gray-200 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 text-gray-700 rounded-xl font-medium transition-all shadow-sm"
          >
            <FaList />
            View Register
          </button>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          
          <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
              <FaClipboardList />
            </div>
            <h3 className="font-semibold text-lg text-gray-800">General Information</h3>
          </div>

          <div className="p-6 lg:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
              <div className="xl:col-span-1">
                <label className={labelClass}>Number of Jobs</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min="0"
                  value={numberOfJobs}
                  onChange={handleNumberOfJobsChange}
                  className={inputClass}
                  placeholder="e.g. 5"
                />
              </div>

              <div className="xl:col-span-1">
                <label className={labelClass}>Challan No</label>
                <input
                  name="challanNo"
                  value={commonData.challanNo}
                  onChange={handleCommonChange}
                  className={inputClass}
                  placeholder="Challan"
                />
              </div>

              <div className="xl:col-span-1">
                <label className={labelClass}>Date</label>
                <input
                  name="date"
                  type="date"
                  value={commonData.date}
                  onChange={handleCommonChange}
                  className={inputClass}
                />
              </div>

              <div className="xl:col-span-1">
                <label className={labelClass}>Vehicle No</label>
                <input
                  name="vehicleNo"
                  value={commonData.vehicleNo}
                  onChange={handleCommonChange}
                  className={`${inputClass} uppercase`}
                  placeholder="MH12 AB 1234"
                />
              </div>

              <div className="xl:col-span-1">
                <label className={labelClass}>Total Oil (Liters)</label>
                <input
                  name="totalOil"
                  type="text"
                  inputMode="decimal"
                  step="0.01"
                  value={commonData.totalOil}
                  onChange={handleCommonChange}
                  className={inputClass}
                  placeholder="0.00"
                />
              </div>

              <div className="xl:col-span-1">
                <label className={labelClass}>Oil Challan No</label>
                <input
                  name="oilChallanNo"
                  value={commonData.oilChallanNo}
                  onChange={handleCommonChange}
                  className={inputClass}
                  placeholder="Oil Challan"
                />
              </div>
            </div>

            {/* Job Entries - Mobile Cards */}
            <div className="sm:hidden space-y-4">
              {formRows.map((row, idx) => (
                <div key={idx} className="border border-gray-100 rounded-xl p-5 shadow-sm bg-white relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                  <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-100">
                    <h3 className="font-bold text-indigo-600">Job #{idx + 1}</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Job No</label>
                      <input value={row.jobNo} onChange={(e) => handleRowChange(idx, 'jobNo', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">KVA</label>
                      <input value={row.kva} onChange={(e) => handleRowChange(idx, 'kva', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Make</label>
                      <input value={row.make} onChange={(e) => handleRowChange(idx, 'make', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Make S.R. No</label>
                      <input value={row.makeSrNo} onChange={(e) => handleRowChange(idx, 'makeSrNo', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Type</label>
                      <select value={row.type} onChange={(e) => handleRowChange(idx, 'type', e.target.value)} className={inputClass}>
                        <option value="CRGO">CRGO</option>
                        <option value="AMDT">AMDT</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Oil Capacity (L)</label>
                      <input type="text" inputMode="decimal" step="0.01" value={row.oilCap} onChange={(e) => handleRowChange(idx, 'oilCap', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">KG (Weight)</label>
                      <input type="text" inputMode="decimal" step="0.01" value={row.kg} onChange={(e) => handleRowChange(idx, 'kg', e.target.value)} className={inputClass} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Job Entries - Desktop Table */}
            {formRows.length > 0 && (
              <div className="hidden sm:block overflow-hidden border border-gray-100 rounded-2xl shadow-sm mt-4">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr>
                      <th className={`${thClass} text-center w-16`}>#</th>
                      <th className={thClass}>Job No</th>
                      <th className={thClass}>KVA</th>
                      <th className={thClass}>Make</th>
                      <th className={thClass}>Make S.R. No</th>
                      <th className={thClass}>Type</th>
                      <th className={thClass}>Oil Capacity (L)</th>
                      <th className={thClass}>KG (Weight)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formRows.map((row, idx) => (
                      <tr key={idx} className="bg-white hover:bg-gray-50/50 transition-colors">
                        <td className="p-4 text-center font-medium text-gray-500">{idx + 1}</td>
                        <td className="p-3"><input value={row.jobNo} onChange={(e) => handleRowChange(idx, 'jobNo', e.target.value)} className={inputClass} /></td>
                        <td className="p-3"><input value={row.kva} onChange={(e) => handleRowChange(idx, 'kva', e.target.value)} className={inputClass} /></td>
                        <td className="p-3"><input value={row.make} onChange={(e) => handleRowChange(idx, 'make', e.target.value)} className={inputClass} /></td>
                        <td className="p-3"><input value={row.makeSrNo} onChange={(e) => handleRowChange(idx, 'makeSrNo', e.target.value)} className={inputClass} /></td>
                        <td className="p-3">
                          <select value={row.type} onChange={(e) => handleRowChange(idx, 'type', e.target.value)} className={inputClass}>
                            <option value="CRGO">CRGO</option>
                            <option value="AMDT">AMDT</option>
                          </select>
                        </td>
                        <td className="p-3"><input type="text" inputMode="decimal" step="0.01" value={row.oilCap} onChange={(e) => handleRowChange(idx, 'oilCap', e.target.value)} className={inputClass} /></td>
                        <td className="p-3"><input type="text" inputMode="decimal" step="0.01" value={row.kg} onChange={(e) => handleRowChange(idx, 'kg', e.target.value)} className={inputClass} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {formRows.length === 0 && (
              <div className="py-12 flex flex-col items-center justify-center text-gray-400 bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-200 mt-4">
                <FaClipboardList className="text-4xl mb-3 text-gray-300" />
                <p>Enter the number of jobs to start adding records.</p>
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="bg-gray-50/80 px-6 py-5 border-t border-gray-100 flex flex-col sm:flex-row justify-end gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-all shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className={`px-8 py-2.5 rounded-xl font-medium text-white flex items-center justify-center gap-2 shadow-sm transition-all ${
                isFormValid() && !isSubmitting
                  ? 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200 hover:-translate-y-0.5'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <FaPrint className="text-sm" />
                  Submit & Print
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntryForm;
